import _ from 'lodash'
import type {
    CompRef,
    Pointer,
    PossibleDataItems,
    PossibleViewModes,
    Rect,
    MeasurementsCallback,
    Measurements,
    Pair,
    EventEmitter,
    Experiment
} from '@wix/document-services-types'
import {
    CreateExtArgs,
    Extension,
    HistoryItem,
    Transaction,
    ExtensionAPI,
    DeepFunctionMap,
    PointerMethods,
    pointerUtils,
    PDAL
} from '@wix/document-manager-core'
import type {CreateViewerExtensionArgument} from '../../types'
import {VIEWER_PAGE_DATA_TYPES, VIEW_MODES, MULTILINGUAL_TYPES, VIEWER_UPDATED_EVENT} from '../../constants/constants'
import type {PageAPI} from '../page'
import {ReportableError, getReportableFromError} from '@wix/document-manager-utils'
import {viewerTransformSet} from './viewerTransformers'
import {displayedOnlyStructureUtil} from '@wix/santa-core-utils'
import type {Callback, ComponentBoundingBox, ComponentWithInnerElement} from '@wix/viewer-manager-interface'
import type {HooksExtensionApi} from '../hooks/hooks'
import {RENDER_HOOKS} from './hooks'

const VIEWER_NAMES = {
    BOLT: 'bolt',
    THUNDERBOLT: 'tb',
    MOCK: 'mock'
}

const documentToViewerTypes = _.assign({}, VIEWER_PAGE_DATA_TYPES, VIEW_MODES, MULTILINGUAL_TYPES, {
    rendererModel: 'rendererModel',
    platform: 'platform',
    wixCode: 'wixCode',
    documentServicesModel: 'documentServicesModel',
    pagesPlatformApplications: 'pagesPlatformApplications'
})

const NAVIGATION_ERROR_IDENTIFIER = 'viewer_extension_navigation_error_identifier'

const viewerManagerTypes = _.keyBy([
    'activeVariants',
    'activeModes',
    'renderFlags',
    'displayedOnlyComponents',
    'customElementsPointer',
    'renderRealTimeConfigPointer',
    'runtime',
    'svgShapes',
    'multilingual',
    'ghostStructure',
    'ghostControllers',
    'blocksPreviewData'
])

export interface InnerViewerExtensionAPI extends DeepFunctionMap {
    getPrimaryPageId(): string
    navigateToPage(pageId: string): Promise<void>
    getCurrentViewMode(): string
    syncViewer(pagesToSync?: PagesToSync, syncOnlyItemsWithoutPage?: boolean): void
    syncViewerFromOpenTransaction(): void
    syncPointers(pointers: Pointer[]): void
    syncPagesToViewer(pageIds: string[], viewMode?: PossibleViewModes): void
    getViewerName(): string
    getViewerVersion(): string
    convertIdToScopedPointer(id: string, enableRepeatersInScopes?: boolean): Pointer
    getRepeaterItemsIndexesById(id: string): number[] | undefined
    getUnderXYIncludingInnerElements(
        x: number,
        y: number
    ): {visibleElements: ComponentWithInnerElement[]; hiddenByBlockingLayer: ComponentWithInnerElement[]}
    getInnerElementBoundingBoxes(path: string[]): ComponentBoundingBox[]
    getComponentMeasurements(compRef: CompRef): Rect
    updateData(actions: {pointer: Pointer; value: PossibleDataItems}[]): void
    isScrollable(pointer: CompRef): boolean
    replaceViewer(pageId?: string): Promise<void>
    waitForViewer(): Promise<void>
}

type ViewerSiteAPI = Record<string, Function>

export interface ViewerExtensionAPI extends ExtensionAPI {
    viewer: InnerViewerExtensionAPI
    siteAPI: ViewerSiteAPI
}

interface PagesToSync {
    [pageId: string]: boolean
}

const createExtension = ({viewerManager}: CreateViewerExtensionArgument): Extension => {
    const {dal: viewerManagerDal, pointers, viewerSiteAPI, actions} = viewerManager
    let pages: PageAPI | null = null

    const pagesApi = () => {
        if (!pages) {
            throw new Error('pagesApi accessed before it was initialized')
        }
        return pages
    }

    const getViewerActions = (
        documentTransaction: Transaction,
        pagesToSync: PagesToSync = {},
        syncOnlyItemsWithoutPage: boolean = false
    ): HistoryItem[] =>
        documentTransaction.items.filter(action => {
            const pageId = _.get(action, ['value', 'metaData', 'pageId'])
            if (syncOnlyItemsWithoutPage) {
                return !pageId && action.key.type !== 'runtime'
            }
            return (
                !pageId ||
                pageId === 'masterPage' ||
                (!_.isEmpty(pagesToSync) ? pagesToSync[pageId] : viewerSiteAPI.getViewerLoadedPagesIds()[pageId])
            )
        })

    const notifyOnViewerUpdate = async (
        viewerActions: HistoryItem[],
        eventEmitter: EventEmitter,
        experimentInstance: Experiment
    ) => {
        if (experimentInstance.isOpen('dm_observables')) {
            await viewerManager.viewerSiteAPI.waitForViewer()
            eventEmitter.emit(
                VIEWER_UPDATED_EVENT,
                viewerActions.map(action => action.key as Pointer)
            )
        }
    }
    const updateViewerManager = (
        createExtArgs: CreateExtArgs,
        viewerActions: HistoryItem[],
        viewMode?: PossibleViewModes
    ) => {
        const {coreConfig, extensionAPI} = createExtArgs
        const {logger} = coreConfig
        const {hooks} = extensionAPI as HooksExtensionApi

        // Excluding non current view modes
        const currentViewMode = viewerManager.viewerSiteAPI.getViewMode()
        const viewModeToFilter = viewMode ?? currentViewMode
        const ignoredViewModes = _(VIEW_MODES)
            .values()
            .filter(_viewMode => _viewMode !== viewModeToFilter)
            .value()

        try {
            // TODO: {@see DM-3109} runInBatch impl in viewer-manager-adapter
            actions.runInBatch(() => {
                viewerActions.forEach(action => {
                    // Only sending specific types and relevant types
                    const {key, value} = action
                    const updateDocumentToViewerType =
                        documentToViewerTypes[key.type] && !ignoredViewModes.includes(key.type as PossibleViewModes)
                    const updateViewerOnlyType = viewerManagerTypes[key.type]
                    if (updateDocumentToViewerType || updateViewerOnlyType) {
                        if (!_.isNil(value)) {
                            viewerTransformSet(createExtArgs, viewerManagerDal, key, value)
                        } else {
                            viewerManagerDal.remove(key)
                        }
                    }
                })
                notifyOnViewerUpdate(viewerActions, createExtArgs.eventEmitter, coreConfig.experimentInstance)
            })
        } catch (err) {
            const reportableError = getReportableFromError(err, {
                message: (err as Error).message,
                errorType: 'viewerBatchFailed'
            })
            logger.captureError(reportableError)
            throw reportableError
        } finally {
            hooks.executeHook(RENDER_HOOKS.AFTER_RENDER.createEvent({}))
        }
    }

    const updateData = (dataActions: {pointer: Pointer; value: PossibleDataItems}[]) => {
        viewerSiteAPI.updateData(dataActions)
    }

    const replaceViewer = async (pageId?: string) => {
        await viewerSiteAPI.replaceViewer(pageId)
    }

    const getMainPageId = (): string => {
        if (!pages) {
            throw new ReportableError({
                errorType: 'PAGES_API_NOT_AVAILABLE',
                message: "The pages api isn't available yet, so we can't get the id of the main page"
            })
        }
        return pagesApi().getMainPageId()
    }

    const getCurrentPageId = (): string => viewerSiteAPI.getPrimaryPageId()

    const getFocusId = (): string => viewerSiteAPI.getFocusedRootId()

    const isFocusedOnPopup = (): boolean => getFocusId() === getCurrentPageId()

    const isFocusBeingDeleted = (viewerActions: HistoryItem[]): boolean =>
        _.some(viewerActions, {
            key: {
                id: getFocusId(),
                type: viewerManager.viewerSiteAPI.getViewMode()
            },
            value: undefined
        })

    const getCurrentViewMode = (): string => viewerSiteAPI.getViewMode()

    const navigateToPage = (pageId: string): Promise<void> => {
        const navInfoPointer = pointers.runtime.getWantedNavInfo()

        return new Promise<void>((resolve, reject) => {
            viewerManagerDal.set(navInfoPointer, {pageId})
            viewerSiteAPI.registerNavigationComplete(resolve)
            viewerSiteAPI.registerNavigationError(NAVIGATION_ERROR_IDENTIFIER, reject)
        }).finally(() => {
            viewerSiteAPI.unregisterNavigationError(NAVIGATION_ERROR_IDENTIFIER)
        })
    }

    const chooseAutoNavigationDestination = (viewerActions: HistoryItem[]): string | null => {
        if (isFocusBeingDeleted(viewerActions)) {
            return isFocusedOnPopup() ? getMainPageId() : getCurrentPageId()
        }
        return null
    }

    const createPostTransactionOperations = (createExtArgs: CreateExtArgs) => ({
        updateViewer: (documentTransaction: Transaction) => {
            const viewerActions = getViewerActions(documentTransaction, {}, false)
            const autoNavigationDestination = chooseAutoNavigationDestination(viewerActions)
            if (autoNavigationDestination) {
                return async () => {
                    await navigateToPage(autoNavigationDestination)
                    updateViewerManager(createExtArgs, viewerActions)
                }
            }
            updateViewerManager(createExtArgs, viewerActions)
        }
    })

    const getViewerActionsAndUpdateViewerManager = (
        createExtArgs: CreateExtArgs,
        documentTransaction: Transaction,
        pagesToSync: PagesToSync = {},
        syncOnlyItemsWithoutPage: boolean = false
    ) => {
        const viewerActions = getViewerActions(documentTransaction, pagesToSync, syncOnlyItemsWithoutPage)
        updateViewerManager(createExtArgs, viewerActions)
    }
    // @ts-expect-error
    const createPointersMethods = (/*dmApis: DmApis*/): PointerMethods => pointers

    const createExtensionAPI = (createExtArgs: CreateExtArgs): ViewerExtensionAPI => {
        const {
            extensionAPI,
            dal,
            coreConfig: {logger}
        } = createExtArgs
        pages = extensionAPI.page as PageAPI
        const siteAPI = viewerSiteAPI as unknown as ViewerSiteAPI

        const syncViewer = (pagesToSync?: PagesToSync, syncOnlyItemsWithoutPage?: boolean) =>
            getViewerActionsAndUpdateViewerManager(
                createExtArgs,
                dal.getTentativeAndAcceptedAsTransaction(),
                pagesToSync,
                syncOnlyItemsWithoutPage
            )

        /* The clone is needed here since bolt is sending these data through window.postMessage, which cannot serialize proxies.
           See ticket https://jira.wixpress.com/browse/DM-5124.
           This can and should be removed when thunderbolt is open to all.
           If the performance impact is too severe, we can relax the restriction in the dal that always exports proxies,
           since this is the only flow that uses `dal.getCurrentOpenTransaction`
         */
        const syncViewerFromOpenTransaction = () =>
            getViewerActionsAndUpdateViewerManager(createExtArgs, _.cloneDeep(dal.getCurrentOpenTransaction()))

        const syncPointers = (ptrs: Pointer[]) => {
            const viewerActions = getViewerActions({
                id: 'syncPointers',
                items: ptrs.map(pointer => ({key: pointer, value: dal.get(pointer)}))
            })
            updateViewerManager(createExtArgs, viewerActions)
        }

        const syncPagesToViewer = (pageIds: string[], viewMode?: PossibleViewModes) => {
            const values = _.flatMap(pageIds, pageId => {
                const pageCompFilter = pagesApi().getPageIndexId(pageId)
                const pageCompInStoreIndex = dal.getIndexed(pageCompFilter)
                return _.flatMap(pageCompInStoreIndex, (namespaceResults, type: string) =>
                    _.map(namespaceResults, (value, id) => ({key: {id, type}, value}))
                )
            })
            updateViewerManager(createExtArgs, values, viewMode)
        }

        const getViewerName = () => viewerManager.viewerConfig.viewerName

        const isBolt = () => getViewerName() === VIEWER_NAMES.BOLT

        const getViewerVersion = () => viewerManager.viewerConfig.viewerVersion

        const getRepeaterItemsIndexesById = (id: string): number[] | undefined => {
            return viewerManager.viewerSiteAPI.getRepeaterItemsIndexes
                ? viewerManager.viewerSiteAPI.getRepeaterItemsIndexes(id)
                : undefined
        }
        const getRepeatedComponents = (pointer: Pointer): Pointer[] | null => {
            if (!viewerManager.viewerSiteAPI?.getRepeatedComponents) {
                return null
            }

            const templatePointer = {...pointer, id: displayedOnlyStructureUtil.getRepeaterTemplateId(pointer.id)}
            return viewerManager.viewerSiteAPI.getRepeatedComponents(templatePointer)
        }

        const convertIdToScopedPointer = (id: string, enableRepeatersInScopes = true) => {
            if (isBolt()) {
                const reportableError = new ReportableError({
                    message: 'Scopes are not available in bolt',
                    errorType: 'useScopesInBolt'
                })
                logger.captureError(reportableError)

                return pointerUtils.getPointer(id, getCurrentViewMode())
            }

            return viewerManager.viewerSiteAPI.convertIdToScopedPointer(id, enableRepeatersInScopes)
        }

        const getUnderXYIncludingInnerElements = (
            x: number,
            y: number
        ): {visibleElements: ComponentWithInnerElement[]; hiddenByBlockingLayer: ComponentWithInnerElement[]} => {
            return viewerManager.viewerSiteAPI.getInnerElementsUnderXY(x, y)
        }

        const getInnerElementBoundingBoxes = (path: string[]): ComponentBoundingBox[] =>
            viewerManager.viewerSiteAPI.getInnerElementBoundingBoxes(path)

        const getComponentMeasurements = (compRef: CompRef): Rect =>
            viewerManager.viewerSiteAPI.getBasicMeasureForComp(compRef)

        const isScrollable = (compRef: CompRef): boolean => viewerManager.viewerSiteAPI.isScrollable(compRef)

        const waitForViewer = async () => viewerManager.viewerSiteAPI.waitForViewer()

        return {
            viewer: {
                getInnerElementBoundingBoxes,
                isScrollable,
                convertIdToScopedPointer,
                getPrimaryPageId: getCurrentPageId,
                navigateToPage,
                getCurrentViewMode,
                syncViewer,
                syncPointers,
                syncViewerFromOpenTransaction,
                syncPagesToViewer,
                getViewerName,
                getViewerVersion,
                getRepeaterItemsIndexesById,
                getRepeatedComponents,
                getUnderXYIncludingInnerElements,
                getComponentMeasurements,
                updateData,
                replaceViewer,
                registerMeasurements: {
                    registerToMeasurementsChange: (handler: Callback) =>
                        viewerSiteAPI.registerToMeasurementsChange(handler)
                },
                waitForViewer
            },
            siteAPI
        }
    }

    const getter = (dal: PDAL, pointer: Pointer) => {
        if (pointer.noRefFallbacks) {
            return dal.get(pointer)
        }

        return viewerManagerDal.get(pointer, false) //purposely not cloned, as the clone will happen in the GeneralSuperDAL
    }
    const setter = ({}, pointer: Pointer, value: any) => viewerManagerDal.set(pointer, value)
    setter.avoidInConflictDetection = true

    const createGetters = () =>
        _(viewerManagerTypes)
            .keyBy(val => val)
            .mapValues(() => getter)
            .value()

    const createPublicAPI = () => {
        const mapToPairs = (compIdToMeasureMap: Record<string, Measurements>): Pair<CompRef, Measurements>[] =>
            Object.entries(compIdToMeasureMap).map(([compId, measurements]) => [
                pointerUtils.getCompPointer(compId, VIEW_MODES.DESKTOP),
                measurements
            ])

        return {
            viewer: {
                measurements: {
                    registerToMeasurementsChange: (handler: MeasurementsCallback) =>
                        viewerSiteAPI.registerToMeasurementsChange((compIdToMeasureMap: Record<string, Measurements>) =>
                            handler(mapToPairs(compIdToMeasureMap))
                        ),
                    getAll: (): Pair<CompRef, Measurements>[] => mapToPairs(viewerSiteAPI.getAllMeasurements())
                }
            }
        }
    }

    return {
        name: 'viewerExtension',
        createPointersMethods,
        createPostTransactionOperations,
        createExtensionAPI,
        createPublicAPI,
        createGetters
    }
}

export {createExtension, documentToViewerTypes, viewerManagerTypes}
